import * as React from 'react'
import styled from 'styled-components'

import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusPriceBox from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import NoPrint from 'components/utils/NoPrint'
import { CurrencyProvider } from 'contexts/currencyContext'
import { useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import type { IntlShape } from 'react-intl'
import {
  getCanViewLuxPlusBenefits,
  getIsLuxPlusEnabled,
} from 'luxPlus/selectors/featureToggle'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'

export type DiscountDisplayType = 'badge' | 'inlineBadge' | 'following' | 'none'

export type Props = {
  depositComponent?: React.ReactNode
  discountDisplayType?: DiscountDisplayType
  discount?: number
  price: number
  luxPlusPrice?: number
  value?: number
  currencyCode: string
  priceLabel: string
  totalLabel?: string
  roomOccupancy?: string
  className?: string
  showTaxesAndFees?: boolean
}

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RoomText = styled.div`
  font-size: 11px;
  font-weight: ${(props) => props.theme.font.primary.weight.regular};
  line-height: 16px;
  color: ${(props) => props.theme.palette.neutral.default.two};
`

export function buildNightsPriceLabel(intl: IntlShape, min: number, max: number) {
  if (min !== max) {
    return intl.formatMessage({
      id: 'nAbRIf4beX_JNuhSni4M',
      defaultMessage: '{min} to {max} nights from',
      description: 'Price label for a range of nights, e.g. "3 to 5 nights from"',
    }, { min, max })
  }
  return intl.formatMessage({
    id: '6KK0bmzirHb-25sBNoMP',
    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    description: 'Price label for a number of nights, e.g. "5 nights from"',
  }, { count: min })
}

export function buildDaysPriceLabel(intl: IntlShape, min: number, max: number) {
  if (min !== max) {
    return intl.formatMessage({
      id: 'PIeI7aG5FlEBEJDdqjXq',
      defaultMessage: '{min} to {max} days from',
      description: 'Price label for a range of days, e.g. "3 to 5 days from"',
    }, { min, max })
  }
  return intl.formatMessage({
    id: 'e6k1Q8InWKnFJThXgCvR',
    defaultMessage: '{count, plural, one {# day} other {# days}} from',
    description: 'Price label for a number of days, e.g. "5 days from"',
  }, { count: min })
}

function PriceDetails({
  depositComponent,
  discountDisplayType = 'following',
  discount,
  price,
  luxPlusPrice,
  value,
  currencyCode,
  priceLabel,
  totalLabel,
  roomOccupancy,
  className,
  showTaxesAndFees = true,
}: Props) {
  const [luxPlusEnabled, canViewLuxPlusBenefits] = useAppSelector(
    (state) => [getIsLuxPlusEnabled(state), getCanViewLuxPlusBenefits(state)],
    shallowEqual,
  )
  const showLuxPlusPricing =
    luxPlusEnabled && !!luxPlusPrice && luxPlusPrice < price

  return (
    <CurrencyProvider currency={currencyCode}>
      <PriceWrapper className={className}>
        <VerticalSpacer gap={4}>
          <OnlyIfChildren as={VerticalSpacer} gap={8}>
            {discountDisplayType === 'badge' && discount && (
              <NoPrint>
                <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={discount}
                />
              </NoPrint>
            )}
            {depositComponent && <span>{depositComponent}</span>}
          </OnlyIfChildren>
          <PriceRowPriceCaption>{priceLabel}</PriceRowPriceCaption>
        </VerticalSpacer>
        {!showLuxPlusPricing && (
          <PriceRowPrice
            size="M"
            price={price}
            saleUnit={totalLabel ?? 'total'}
            discountPercentage={
              discountDisplayType === 'inlineBadge' ? discount : undefined
            }
            discountType="estimate"
          />
        )}
        {showLuxPlusPricing && canViewLuxPlusBenefits && (
          <VerticalSpacer gap={4}>
            <PriceRowLuxPlusPriceBox
              size="S"
              memberPrice={luxPlusPrice}
              saleUnit={totalLabel ?? 'total'}
            />
            <PriceRowLuxPlusNonMemberPrice nonMemberPrice={price} />
          </VerticalSpacer>
        )}
        {showLuxPlusPricing && !canViewLuxPlusBenefits && (
          <>
            <PriceRowPrice
              size="S"
              price={price}
              saleUnit={totalLabel ?? 'total'}
              discountPercentage={
                discountDisplayType === 'inlineBadge' ? discount : undefined
              }
              discountType="estimate"
            />
            <PriceRowLuxPlusPrice
              size="S"
              memberPrice={luxPlusPrice}
              saleUnit={totalLabel ?? 'total'}
              logoPlacement="trailing"
            />
          </>
        )}
        {discountDisplayType === 'following' && !!value && !!discount && (
          <NoPrint>
            <PriceRowValueDiscountWithCaption
              size="L"
              originalValue={Math.ceil(value)}
              discountPercentage={discount}
            />
          </NoPrint>
        )}
        {roomOccupancy && <RoomText>{roomOccupancy}</RoomText>}
        {showTaxesAndFees && <PriceRowTaxesAndFees />}
      </PriceWrapper>
    </CurrencyProvider>
  )
}
export default PriceDetails
